<template>
  <div>
    <naj-box
      v-if="showJudgements"
      :title="local.home.tabs.judgements"
    >
      <div 
        v-if="!parentJudgements || parentJudgements.length === 0"
        class="empty-state-message"
      >
        {{ local.bankruptcy.subRequests.noJudgements }}
      </div>
      <v-row v-else>
        <v-col
          v-for="(judgement, index) in parentJudgements"
          :key="judgement.id || judgement.code || index"
          cols="12"
          md="6"
          lg="4"
        >
          <bankruptcy-card
            :info-item="judgement"
            :icon="'mdi-gavel'"
            :header="local.caseFile.cards.judgement.header"
            :code-display-name="local.caseFile.cards.judgement.codeDisplayName"
            :code="judgement.code"
            :status="judgement.judgementStatus"
            :status-display-name="local.caseFile.cards.judgement.statusDisplayName"
            :date="judgement.conclusiveDate"
            :date-display-name="local.caseFile.cards.judgement.dateDisplayName"
            :has-actions="false"
            :court-name="judgement.courtName"
            :sub-circle-name="judgement.subCircleName"
            @card-click="viewAttachment(judgement)"
          >
          </bankruptcy-card>
        </v-col>
      </v-row>
    </naj-box>

    <naj-box
      v-if="showSessions"
      :title="local.bankruptcy.bankruptcyFile.sessions.tabTitle"
    >
      <div 
        v-if="!parentSessions || parentSessions.length === 0"
        class="empty-state-message"
      >
        {{ local.bankruptcy.subRequests.noSessions }}
      </div>
      <v-row v-else>
        <v-col
          v-for="(session, index) in parentSessions"
          :key="session.id || session.code || session.objectKey || index"
          cols="12"
          md="6"
          lg="4"
        >
          <bankruptcy-card
            :info-item="session"
            :icon="'mdi-account-group'"
            :header="session.sessionType"
            :status="session.sessionStatus"
            :status-display-name="local.caseFile.cards.session.statusDisplayName"
            :date="session.sessionDate"
            :date-display-name="local.caseFile.cards.session.dateDisplayName"
            :has-actions="false"
            :court-name="session.court"
            :sub-circle-name="session.subCircle"
          >
            <template slot="bottom-slot">
              <div class="row px-2 header-text">
                <div class="py-1 text-c-green col-4">
                  {{ local.caseFile.cards.session.codeDisplayName }}
                </div>
                <template
                  v-if="session.isVideoConference"
                  class="py-1 col-8"
                >
                  <span class="py-1 text-info-value col-3">{{ local.caseFile.cards.sessionTypes.videoSession }}</span>
                  <span
                    v-if="session.videoConferenceUrl"
                    class="videoConferenceUrl"
                    @click="copyURL(session.videoConferenceUrl)"
                  > {{ local.home.upcomingSessions.copyLink }} </span>
                  <v-icon
                    :style="{ color: 'var(--app-primary-color)' }"
                    class="copy-icon mx-0"
                    left
                    @click="copyURL(session.videoConferenceUrl)"
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                <template
                  v-else-if="session.typeId == sessionTypes.remoteArgument"
                  class="py-1 text-info-value col-8"
                >
                  <span class="py-1 text-info-value col-2">{{ local.caseFile.cards.sessionTypes.chatSession }}</span>
                </template>
                <template
                  v-else
                  class="py-1 col-8"
                >
                  <span class="py-1 text-info-value col-3">{{ local.caseFile.cards.sessionTypes.attendance }}</span>
                  <span class="text-info-value"> {{ local.home.upcomingSessions.courtLocation }} </span>
                  <v-icon
                    :style="{ color: 'var(--app-primary-color)' }"
                    class="copy-icon mx-0"
                    left
                  >
                    mdi-map-marker
                  </v-icon>
                </template>
              </div>

              <div class="row px-2 header-text">
                <div class="py-1 text-c-green col-4">
                  {{ local.caseFile.cards.session.timeDisplayName }}
                </div>
                <div
                  v-if="session.appointmentStartDate && session.appointmentEndDate"
                  class="py-1 text-info-value col-8"
                >
                  {{ getTime12Format(session.appointmentStartDate) }}{{ isAm(session.appointmentStartDate)? local.calendar.amShort: local.calendar.pmShort }}
                  -
                  {{ getTime12Format(session.appointmentEndDate) }}{{ isAm(session.appointmentEndDate)? local.calendar.amShort: local.calendar.pmShort }}
                  <span
                    v-if="session.appointmentDurationInMinutes"
                    class="text-c-lite"
                  >
                    ( {{ session.appointmentDurationInMinutes }}) {{ local.calendar.min }}
                  </span>
                </div>
                <div
                  v-else
                  class="py-1 text-info-value col-8"
                >
                  --
                </div>
              </div>
            </template>
          </bankruptcy-card>
        </v-col>
      </v-row>
    </naj-box>

    <pdf-modal
      v-if="selectedFile"
      ref="pdfModal"
      :file-name="selectedFile.fileName"
      :share-point-id="selectedFile.spid"
      @modalClose="hideViewAttachmentModal"
    >
    </pdf-modal>
  </div>
</template>

<script>
import { sessionTypes } from '@/helpers/enum';
import { getTime12Format, isAm } from '@/helpers/utils';
import BankruptcyCard from '../bankruptcy-file/bankruptcy-card.vue';
import PdfModal from '@/components/shared/pdf-modal/index.vue';

export default {
  name: 'ParentRequestInfo',
  components: {
    BankruptcyCard,
    PdfModal,
  },
  props: {
    parentJudgements: {
      type: Array,
      default: () => [],
    },
    parentSessions: {
      type: Array,
      default: () => [],
    },
    showJudgements: {
      type: Boolean,
      default: true,
    },
    showSessions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedFile: null,
      sessionTypes,
      getTime12Format,
      isAm,
    };
  },
  methods: {
    viewAttachment(judgement) {
      if (judgement.spid) {
        this.selectedFile = judgement;
        this.$nextTick(() => {
          const modal = this.$refs.pdfModal;
          modal.showModal();
        });
      }
    },
    
    hideViewAttachmentModal() {
      this.selectedFile = null;
    },

    async copyURL(videoConferenceUrl) {
      if (videoConferenceUrl) {
        await navigator.clipboard.writeText(videoConferenceUrl);
        this.toaster.success(this.local.home.upcomingSessions.copied);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.copy-icon {
  font-size: 20px;
}

.videoConferenceUrl {
  color: var(--app-primary-color);
  cursor: pointer;
}

.header-text {
  font-weight: normal;
  font-size: 18px;
  color: var(--app-dark-primary-color);
}

.text-c-green {
  color: var(--app-primary-color);
}

.text-c-lite {
  color: #a6b8bb;
  font-weight: bold;
}

.text-info-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: bold;
}

.empty-state-message {
  text-align: center;
  padding: 60px 20px;
  font-size: 20px;
  font-weight: bold;
}
</style>
